'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';
import DemoDataPill from './DemoDataPill';

// ── Types ──────────────────────────────────────────────────────────────────

export interface EscalatedTicket {
  id: string;
  title: string;
  job: string;
  priority: string;
  // SLA-age + assignee additions; orchestrator passes what it has.
  created_at?: string;
  assigned_to_name?: string;
}

interface TeamMember {
  id: string;
  first_name: string;
  last_name: string;
  job_title: string | null;
  avatar_url: string | null;
}

interface Row5Props {
  escalations: EscalatedTicket[];
}

// ── Team Directory: Mike's direct reports ──────────────────────────────────
// Filter by last_name on the employees table. If the names change, this
// query will return 0 rows and the empty-state will surface the staleness.
const DIRECT_REPORT_LAST_NAMES = ['Skelton', 'Gamkong', 'Kimble'];

// ── SLA-age pill ───────────────────────────────────────────────────────────

function slaAge(createdAt?: string): { label: string; bg: string; fg: string } {
  if (!createdAt) return { label: 'New', bg: 'rgba(16,185,129,0.10)', fg: '#10B981' };
  const ageDays = (Date.now() - new Date(createdAt).getTime()) / 86_400_000;
  if (ageDays > 7) return { label: `${Math.floor(ageDays)}d`, bg: 'rgba(239,68,68,0.10)', fg: '#EF4444' };
  if (ageDays >= 3) return { label: `${Math.floor(ageDays)}d`, bg: 'rgba(245,158,11,0.10)', fg: '#F59E0B' };
  if (ageDays >= 1) return { label: `${Math.floor(ageDays)}d`, bg: 'rgba(16,185,129,0.10)', fg: '#10B981' };
  return { label: 'today', bg: 'rgba(16,185,129,0.10)', fg: '#10B981' };
}

function priorityColor(priority: string): { bg: string; fg: string } {
  const p = priority.toLowerCase();
  if (p === 'critical' || p === 'high') return { bg: 'rgba(239,68,68,0.10)', fg: '#EF4444' };
  if (p === 'medium')                    return { bg: 'rgba(245,158,11,0.10)', fg: '#F59E0B' };
  return { bg: 'rgba(7,176,233,0.10)', fg: '#07B0E9' };
}

// ── Retry-on-429 / timeout wrapper for the employees fetch ─────────────────

async function withRetry<T>(
  fn: () => Promise<T>,
  signal: AbortSignal,
  maxAttempts = 3,
  timeoutMs = 8000,
): Promise<T> {
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
    const timeoutPromise = new Promise<never>((_, reject) =>
      setTimeout(
        () => reject(new Error(`employees query timed out (attempt ${attempt + 1}/${maxAttempts})`)),
        timeoutMs,
      ),
    );
    try {
      return await Promise.race([fn(), timeoutPromise]);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      if ((err as { name?: string })?.name === 'AbortError' || signal.aborted) throw err;
      const isRate = msg.includes('429') || msg.toLowerCase().includes('too many');
      const isTimeout = msg.includes('timed out');
      console.warn(`[TeamDirectory] attempt ${attempt + 1} failed: ${msg}`);
      if (attempt === maxAttempts - 1 || (!isRate && !isTimeout)) throw err;
      await new Promise((r) => setTimeout(r, 1000 * Math.pow(2, attempt))); // 1s/2s/4s
    }
  }
  throw new Error('[TeamDirectory] exhausted retry attempts');
}

// ── Mock HR Quick Actions ──────────────────────────────────────────────────

const MOCK_PTO: { name: string; range: string; daysAgo: number }[] = [];

const MOCK_CERTS: { name: string; cert: string; daysLeft: number }[] = [];

const MOCK_REVIEWS: { name: string; dueLabel: string }[] = [];

// ── Component ──────────────────────────────────────────────────────────────

export default function Row5ExceptionsPeople({ escalations }: Row5Props) {
  return (
    <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <EscalationsCard escalations={escalations} />
      <TeamDirectoryCard />
      <HRQuickActionsCard />
    </section>
  );
}

// ── Sub: Escalations ───────────────────────────────────────────────────────

function EscalationsCard({ escalations }: { escalations: EscalatedTicket[] }) {
  const visible = escalations.slice(0, 6);
  return (
    <div
      className="rounded-xl p-5"
      style={{ background: '#FFFFFF', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', border: '1px solid #F0F0F0' }}
    >
      <div className="flex items-center gap-2 mb-3">
        <Icon name="ExclamationTriangleIcon" size={15} style={{ color: '#EF4444' }} />
        <h3 className="text-base font-bold" style={{ color: '#000150', fontFamily: 'Inter, Poppins, sans-serif' }}>
          Escalations
        </h3>
        <span className="ml-auto text-xs text-gray-400">{escalations.length} open</span>
      </div>
      {visible.length === 0 ? (
        <div className="py-8 text-center">
          <Icon name="CheckCircleIcon" size={28} style={{ color: '#10B981' }} className="mx-auto mb-2" />
          <p className="text-xs text-gray-500">No open escalations</p>
        </div>
      ) : (
        <div className="space-y-2">
          {visible.map((t) => {
            const sla = slaAge(t.created_at);
            const pri = priorityColor(t.priority);
            return (
              <div key={t.id} className="rounded-lg px-3 py-2" style={{ border: '1px solid #F3F4F6' }}>
                <div className="flex items-start justify-between gap-2">
                  <p className="text-xs font-semibold text-gray-800 line-clamp-1">{t.title}</p>
                  <span
                    className="rounded-full px-2 py-0.5 text-[10px] font-bold whitespace-nowrap"
                    style={{ background: sla.bg, color: sla.fg }}
                    title="SLA age"
                  >
                    {sla.label}
                  </span>
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-[11px] font-mono text-gray-500">{t.job}</span>
                  <span
                    className="rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide"
                    style={{ background: pri.bg, color: pri.fg }}
                  >
                    {t.priority}
                  </span>
                  <span className="ml-auto text-[11px] text-gray-500 truncate">
                    {t.assigned_to_name || 'Unassigned'}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Sub: Team Directory (real query, empty-state on failure) ───────────────

function TeamDirectoryCard() {
  const supabase = createClient();
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const load = useCallback(async () => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError(null);
    console.log('[TeamDirectory] fetching direct reports:', DIRECT_REPORT_LAST_NAMES);

    try {
      const data = await withRetry(async () => {
        const { data, error } = await supabase
          .from('employees')
          .select('id, first_name, last_name, job_title, avatar_url')
          .in('last_name', DIRECT_REPORT_LAST_NAMES);
        if (error) throw error;
        return data ?? [];
      }, controller.signal);

      const filtered = (data as TeamMember[]).filter((m) => m && m.id);
      setMembers(filtered);
      console.log('[TeamDirectory] received', filtered.length, 'members');
    } catch (e: unknown) {
      if ((e as { name?: string })?.name === 'AbortError') return;
      const msg = (e as { message?: string })?.message ?? 'Failed to load team';
      console.error('[TeamDirectory] error', msg);
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [supabase]);

  useEffect(() => {
    load();
    return () => { abortRef.current?.abort(); };
  }, [load]);

  return (
    <div
      className="rounded-xl p-5"
      style={{ background: '#FFFFFF', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', border: '1px solid #F0F0F0' }}
    >
      <div className="flex items-center gap-2 mb-3">
        <Icon name="UsersIcon" size={15} style={{ color: '#10B981' }} />
        <h3 className="text-base font-bold" style={{ color: '#000150', fontFamily: 'Inter, Poppins, sans-serif' }}>
          Direct Reports
        </h3>
      </div>
      {loading ? (
        <div className="py-6 flex items-center justify-center">
          <div className="w-5 h-5 border-2 border-[#07B0E9] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : error || members.length === 0 ? (
        <div className="py-6 text-center">
          <Icon name="ExclamationCircleIcon" size={26} style={{ color: '#F59E0B' }} className="mx-auto mb-2" />
          <p className="text-xs font-semibold text-gray-700">Couldn't load team</p>
          <p className="text-[11px] text-gray-500 mt-0.5">{error ? 'Check connection or schema' : 'No direct reports matched'}</p>
          <button
            onClick={load}
            className="mt-3 text-xs font-semibold rounded-lg px-3 py-1.5 hover:opacity-90 active:scale-95 transition-all"
            style={{ background: '#07B0E9', color: '#FFFFFF' }}
          >
            Retry
          </button>
        </div>
      ) : (
        <>
          <div className="space-y-2.5">
            {members.map((m) => (
              <TeamMemberRow key={m.id} member={m} />
            ))}
          </div>
          <a
            href="/employee-directory"
            className="mt-3 inline-flex items-center gap-1 text-[11px] font-semibold rounded-lg px-2.5 py-1.5 hover:opacity-90 active:scale-95 transition-all"
            style={{ background: 'rgba(7,176,233,0.10)', color: '#0369A1' }}
          >
            View full directory
            <Icon name="ArrowRightIcon" size={10} />
          </a>
        </>
      )}
    </div>
  );
}

function TeamMemberRow({ member }: { member: TeamMember }) {
  const initials = `${(member.first_name || '?').charAt(0)}${(member.last_name || '?').charAt(0)}`.toUpperCase();

  return (
    <div
      className="flex items-center gap-3 rounded-lg px-2.5 py-2 hover:bg-gray-50 transition-colors"
      style={{ border: '1px solid #F3F4F6' }}
    >
      <div
        className="w-9 h-9 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
        style={{ background: 'linear-gradient(135deg, #07B0E9 0%, #033494 100%)' }}
      >
        {initials}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-xs font-semibold text-gray-800 truncate">
          {member.first_name} {member.last_name}
        </p>
        <p className="text-[11px] text-gray-500 truncate">{member.job_title || '—'}</p>
      </div>
      <button
        type="button"
        className="rounded-lg px-2.5 py-1 text-[11px] font-semibold flex items-center gap-1 hover:opacity-90 active:scale-95 transition-all"
        style={{ background: 'rgba(7,176,233,0.10)', color: '#07B0E9' }}
        suppressHydrationWarning
      >
        <Icon name="ChatBubbleLeftEllipsisIcon" size={11} />
        Message
      </button>
    </div>
  );
}

// ── Sub: HR Quick Actions (mock) ───────────────────────────────────────────

function HRQuickActionsCard() {
  return (
    <div
      className="relative rounded-xl p-5"
      style={{ background: '#FFFFFF', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', border: '1px solid #F0F0F0' }}
    >
      <DemoDataPill />
      <div className="flex items-center gap-2 mb-3">
        <Icon name="UserGroupIcon" size={15} style={{ color: '#8B5CF6' }} />
        <h3 className="text-base font-bold" style={{ color: '#000150', fontFamily: 'Inter, Poppins, sans-serif' }}>
          HR Quick Actions
        </h3>
      </div>

      {/* PTO pending */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <p className="text-[11px] uppercase tracking-wider font-semibold text-gray-500">
            PTO pending your approval
          </p>
          <span
            className="rounded-full px-2 py-0.5 text-[10px] font-bold"
            style={{ background: 'rgba(245,158,11,0.10)', color: '#F59E0B' }}
          >
            {MOCK_PTO.length}
          </span>
        </div>
        <div className="space-y-1.5">
          {MOCK_PTO.map((p) => (
            <div key={p.name} className="flex items-center justify-between text-xs">
              <span className="text-gray-700 font-medium truncate">{p.name}</span>
              <span className="text-gray-500">{p.range}</span>
            </div>
          ))}
        </div>
        <a
          href="/hr"
          className="text-[11px] font-semibold mt-2 inline-flex items-center gap-1"
          style={{ color: '#07B0E9' }}
        >
          Review all
          <Icon name="ArrowRightIcon" size={10} />
        </a>
      </div>

      {/* Certs expiring */}
      <div className="pt-3 border-t border-gray-100">
        <div className="flex items-center justify-between mb-2">
          <p className="text-[11px] uppercase tracking-wider font-semibold text-gray-500">
            Certs expiring &lt; 30 days
          </p>
          <span
            className="rounded-full px-2 py-0.5 text-[10px] font-bold"
            style={{ background: 'rgba(239,68,68,0.10)', color: '#EF4444' }}
          >
            {MOCK_CERTS.length}
          </span>
        </div>
        <div className="space-y-1.5">
          {MOCK_CERTS.map((c) => (
            <div key={c.name} className="flex items-center justify-between text-xs">
              <span className="text-gray-700 font-medium truncate">
                {c.name} <span className="text-gray-400 font-normal">· {c.cert}</span>
              </span>
              <span className="text-gray-500">{c.daysLeft}d left</span>
            </div>
          ))}
        </div>
      </div>

      {/* Next reviews due */}
      <div className="pt-3 mt-3 border-t border-gray-100">
        <div className="flex items-center justify-between mb-2">
          <p className="text-[11px] uppercase tracking-wider font-semibold text-gray-500">
            Next reviews due
          </p>
          <span
            className="rounded-full px-2 py-0.5 text-[10px] font-bold"
            style={{ background: 'rgba(139,92,246,0.10)', color: '#8B5CF6' }}
          >
            {MOCK_REVIEWS.length}
          </span>
        </div>
        <div className="space-y-1.5">
          {MOCK_REVIEWS.map((r) => (
            <div key={r.name} className="flex items-center justify-between text-xs">
              <span className="text-gray-700 font-medium truncate">{r.name}</span>
              <span className="text-gray-500">{r.dueLabel}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
